/**
 * Challenge/response auth.
 *
 * Protocol v2 splits the client's keys into two Ed25519 pairs:
 *   - identity key (long-term): derives userId, signs only at /auth/claim
 *     and /auth/rotate-auth. Never signs a daily sign-in challenge.
 *   - auth key (rotatable): signs /auth/verify challenges. Shorter blast
 *     radius if compromised; can be rotated without admin re-approval.
 *
 * The server emits a nonce + expiry; the client signs
 * `canonicalChallenge(challenge)` with their *auth* key and returns both
 * (authPublicKey, signature) plus the *identityPublicKey* as a lookup
 * hint. The server looks up `identity:<identityPublicKey>`, verifies the
 * returned binding's authPublicKey matches, and verifies the Ed25519
 * signature against it. Only then does it mint a session, with
 * userId derived from identityPublicKey.
 *
 * The server never learns either private key — this is purely a
 * signature-based handshake, no PAKE.
 */
import { ProtocolError } from './errors.js';
import { requireBase64UrlString, requireInteger, requireObject } from './parse.js';

export const CHALLENGE_DOMAIN = 'chat/auth/v2';

export interface AuthChallenge {
  /** base64url nonce, >= 16 bytes of entropy. */
  readonly challenge: string;
  /** ms-since-epoch when the server minted this challenge. */
  readonly issuedAt: number;
  /** ms-since-epoch after which the challenge is no longer accepted. */
  readonly expiresAt: number;
}

export interface AuthResponse {
  /** The original challenge bytes, base64url. */
  readonly challenge: string;
  /** base64url Ed25519 signature over `canonicalChallenge(...)` by the auth key. */
  readonly signature: string;
  /** base64url Ed25519 auth public key. Used to verify signature. */
  readonly publicKey: string;
}

/**
 * Canonical byte string signed by the client. Length-prefixed binary
 * encoding so the parts can never alias across separator characters.
 * Each field is encoded as `[2-byte big-endian length][UTF-8 bytes]`.
 * The domain separator lives in the first field; any protocol version
 * bump MUST change it (currently `chat/auth/v2`).
 */
export function canonicalChallenge(challenge: AuthChallenge): Uint8Array {
  const enc = new TextEncoder();
  const parts = [
    enc.encode(CHALLENGE_DOMAIN),
    enc.encode(challenge.challenge),
    enc.encode(String(challenge.issuedAt)),
    enc.encode(String(challenge.expiresAt)),
  ];
  let total = 0;
  for (const p of parts) total += 2 + p.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    if (p.length > 0xffff) throw new Error('canonical field too long');
    out[off] = (p.length >>> 8) & 0xff;
    out[off + 1] = p.length & 0xff;
    out.set(p, off + 2);
    off += 2 + p.length;
  }
  return out;
}

export function parseAuthChallenge(input: unknown): AuthChallenge {
  const obj = requireObject(input, 'challenge');
  const challenge = requireBase64UrlString(obj['challenge'], 'challenge.challenge', {
    minBytes: 16,
  });
  const issuedAt = requireInteger(obj['issuedAt'], 'challenge.issuedAt');
  const expiresAt = requireInteger(obj['expiresAt'], 'challenge.expiresAt');
  if (expiresAt <= issuedAt) {
    throw new ProtocolError('E_RANGE', 'expiresAt must be after issuedAt', 'challenge.expiresAt');
  }
  return { challenge, issuedAt, expiresAt };
}

/**
 * Canonical signing message for `/auth/rotate-auth`.
 *
 * The identity key signs this to bind a *new* auth public key to its
 * identity. The server verifies with the stored identityPublicKey,
 * then overwrites `identity:<identityPublicKey>`'s `authPublicKey`
 * field. An `issuedAt` timestamp bounds replays; the server rejects
 * rotations whose timestamp is stale.
 *
 * Length-prefixed binary encoding matches `canonicalChallenge`.
 * Domain separator `chat/auth-rotate/v2` keeps these signatures
 * from ever being interchangeable with /auth/verify signatures
 * (which use `chat/auth/v2`).
 */
export const ROTATE_AUTH_DOMAIN = 'chat/auth-rotate/v2';

export interface RotateAuthMessage {
  readonly identityPublicKey: string;
  readonly newAuthPublicKey: string;
  /** ms-since-epoch when the client minted this request. */
  readonly issuedAt: number;
}

export function canonicalRotateAuth(m: RotateAuthMessage): Uint8Array {
  const enc = new TextEncoder();
  const parts = [
    enc.encode(ROTATE_AUTH_DOMAIN),
    enc.encode(m.identityPublicKey),
    enc.encode(m.newAuthPublicKey),
    enc.encode(String(m.issuedAt)),
  ];
  let total = 0;
  for (const p of parts) total += 2 + p.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    if (p.length > 0xffff) throw new Error('canonical field too long');
    out[off] = (p.length >>> 8) & 0xff;
    out[off + 1] = p.length & 0xff;
    out.set(p, off + 2);
    off += 2 + p.length;
  }
  return out;
}

export function parseAuthResponse(input: unknown): AuthResponse {
  const obj = requireObject(input, 'authResponse');
  const challenge = requireBase64UrlString(obj['challenge'], 'authResponse.challenge', {
    minBytes: 16,
  });
  const signature = requireBase64UrlString(obj['signature'], 'authResponse.signature', {
    minBytes: 64,
    maxBytes: 64,
  });
  const publicKey = requireBase64UrlString(obj['publicKey'], 'authResponse.publicKey', {
    minBytes: 32,
    maxBytes: 32,
  });
  return { challenge, signature, publicKey };
}
